import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Globe, Layers, Plus, Search, X } from 'lucide-react';
import Fuse from 'fuse.js';
import { cn } from '../../utils/cn';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { useRef } from 'react';

export interface OverlayOption {
  id: string;
  label: string;
  hint?: string;
  /** Sibling regions of the same cluster sort first — comparing them is the common case. */
  isSibling?: boolean;
}

interface OverlayPickerProps {
  /** Everything that can be added, excluding the primary series. */
  options: OverlayOption[];
  selected: string[];
  onToggle: (id: string) => void;
  /** Network total is a separate concept from another server, so it gets its own control. */
  networkOverlay: boolean;
  onToggleNetwork: () => void;
  className?: string;
}

const MAX_OVERLAYS = 6;

/**
 * Add other servers, or the network total, as extra series on the chart.
 *
 * Sibling regions of the same cluster surface first because comparing a pack's regions is the
 * common case: one region dipping while the others hold is a very different problem from all of
 * them dipping together.
 */
export const OverlayPicker = memo(
  ({
    options,
    selected,
    onToggle,
    networkOverlay,
    onToggleNetwork,
    className,
  }: OverlayPickerProps) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);

    useOnClickOutside(containerRef, () => setOpen(false));

    // The dropdown stays open while you add several servers, so it needs a way out that is not
    // "click somewhere else" — otherwise it sits over the controls beside it.
    const close = useCallback(() => {
      setOpen(false);
      setQuery('');
    }, []);

    useEffect(() => {
      if (!open) return;
      const onKey = (event: KeyboardEvent) => {
        if (event.key === 'Escape') close();
      };
      document.addEventListener('keydown', onKey);
      return () => document.removeEventListener('keydown', onKey);
    }, [open, close]);

    const fuse = useMemo(
      () => new Fuse(options, { keys: ['label', 'id'], threshold: 0.35, ignoreLocation: true }),
      [options],
    );

    const results = useMemo(() => {
      const list = query.trim() ? fuse.search(query.trim()).map((hit) => hit.item) : options;
      // Keep the fuzzy ranking within each group, but float siblings to the top when browsing.
      const ordered = query.trim()
        ? list
        : [...list].sort((a, b) => Number(b.isSibling ?? false) - Number(a.isSibling ?? false));
      return ordered.slice(0, 40);
    }, [query, fuse, options]);

    const atLimit = selected.length >= MAX_OVERLAYS;

    return (
      <div className={cn('flex flex-wrap items-center gap-2', className)} ref={containerRef}>
        <button
          type="button"
          className={cn('glass-button-sm', networkOverlay && 'border-accent-400')}
          onClick={onToggleNetwork}
          aria-pressed={networkOverlay}
          title="Overlay the whole network's player count"
        >
          <Globe className="w-4 h-4" />
          Network total
        </button>

        {selected.map((id) => {
          const option = options.find((o) => o.id === id);
          return (
            <span key={id} className="chip chip-removable">
              {option?.label ?? id}
              <button type="button" onClick={() => onToggle(id)} aria-label={`Remove ${option?.label ?? id}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          );
        })}

        <div className="relative">
          <button
            type="button"
            className="glass-button-sm"
            onClick={() => setOpen((prev) => !prev)}
            disabled={atLimit}
            title={atLimit ? `At most ${MAX_OVERLAYS} comparison series` : 'Compare another server'}
          >
            <Plus className="w-4 h-4" />
            Compare
          </button>

          {open && (
            <div className="absolute left-0 top-full mt-1 z-20 w-72 glass-card p-2 shadow-popover">
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
                <input
                  type="text"
                  className="form-input pl-9"
                  placeholder="Search servers..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  autoFocus
                  aria-label="Search servers to compare"
                />
              </div>

              <div className="max-h-64 overflow-y-auto divide-y divide-glass-200/10">
                {results.length === 0 ? (
                  <p className="text-xs text-glass-500 p-3 text-center">No servers match.</p>
                ) : (
                  results.map((option, i) => (
                    <button
                      key={option.id}
                      type="button"
                      className={cn(
                        'w-full text-left px-2 py-2 rounded-lg hover:bg-glass-200/10 transition-colors',
                        selected.includes(option.id) && 'row-active',
                      )}
                      onClick={() => {
                        onToggle(option.id);
                        setQuery('');
                      }}
                    >
                      <span className="block text-sm text-glass-300 truncate">{option.label}</span>
                      {option.hint && (
                        <span className="block text-xs text-glass-500 truncate">{option.hint}</span>
                      )}
                      {/* A quiet divider where the cluster's own regions end. */}
                      {!query.trim() &&
                        option.isSibling &&
                        !results[i + 1]?.isSibling &&
                        i < results.length - 1 && (
                          <span className="eyebrow block pt-2 mt-1 border-t border-glass-200/10">
                            All servers
                          </span>
                        )}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {selected.length > 0 && (
          <span className="text-xs text-glass-400 inline-flex items-center gap-1">
            <Layers className="w-3.5 h-3.5" />
            {selected.length + 1 + (networkOverlay ? 1 : 0)} series
          </span>
        )}
      </div>
    );
  },
);

OverlayPicker.displayName = 'OverlayPicker';
